// GroupPicker — assign a device to a custom tag-group (logical group), or
// create a new group on the fly. Used by the "Assign to group…" action.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { X, Plus } from 'lucide-react';
import { logicalGroupsApi, type LogicalGroup } from '@/lib/inventory-api';

export function GroupPicker({
  onClose, onAssign,
}: {
  onClose: () => void;
  onAssign: (groupId: string) => void;
}) {
  const { t } = useTranslation('components');
  const { data: groups, refetch } = useQuery({ queryKey: ['logical-groups'], queryFn: logicalGroupsApi.list, retry: false });
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const g = await logicalGroupsApi.create({ name: newName.trim(), type: 'tag' });
      await refetch();
      setCreating(false); setNewName('');
      onAssign(g.id);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div className="tk-modal w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">{t('groupPicker.title')}</h2>
          <button className="tk-btn-ghost px-2" onClick={onClose} aria-label={t('groupPicker.actions.close')}><X size={16} /></button>
        </div>
        <ul className="mb-3 max-h-64 space-y-1 overflow-auto">
          {(groups ?? []).map((g: LogicalGroup) => (
            <li key={g.id}>
              <button
                onClick={() => onAssign(g.id)}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm ring-1 ring-white/10 hover:bg-white/5"
              >
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: g.color ?? '#6366f1' }} />
                <span className="truncate">{g.name}</span>
                <span className="ml-auto text-xs text-muted">{g.type}</span>
              </button>
            </li>
          ))}
          {(groups ?? []).length === 0 && <li className="text-sm text-muted">{t('groupPicker.empty')}</li>}
        </ul>
        {creating ? (
          <div className="flex gap-2">
            <input
              autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
              placeholder={t('groupPicker.namePlaceholder')}
              className="flex-1 rounded-md bg-white/5 px-3 py-2 text-sm ring-1 ring-white/10 focus:outline-none focus:ring-primary/50"
              onKeyDown={(e) => { if (e.key === 'Enter') void create(); }}
            />
            <button className="tk-btn-primary" onClick={create} disabled={busy}>{busy ? '…' : t('groupPicker.actions.createAndAssign')}</button>
          </div>
        ) : (
          <button className="tk-btn-ghost w-full justify-center" onClick={() => setCreating(true)}>
            <Plus size={14} /> {t('groupPicker.actions.newGroup')}
          </button>
        )}
      </div>
    </div>
  );
}
